import React, { useState, useEffect } from 'react';
import {
  Card, Steps, Form, Input, Select, DatePicker, Button,
  Typography, Alert, Table, Tag, Row, Col, Divider
} from 'antd';
import {
  ProjectOutlined, TeamOutlined, DatabaseOutlined,
  SettingOutlined, PlusOutlined, CheckCircleOutlined,
  ApartmentOutlined, UserAddOutlined, ToolOutlined
} from '@ant-design/icons';
import axios from 'axios';

const { Title, Text } = Typography;
const API = 'https://q-pmc.com/api/v1';

const getHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('access_token')}`
});

const AdminPage: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [programs, setPrograms] = useState<any[]>([]);
  const [existingProjects, setExistingProjects] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [roles, setRoles] = useState<any[]>([]);
  const [createdProgram, setCreatedProgram] = useState<any>(null);
  const [createdProject, setCreatedProject] = useState<any>(null);
  const [hierarchyNodes, setHierarchyNodes] = useState<any[]>([]);
  const [assignments, setAssignments] = useState<any[]>([]);
  const [activities, setActivities] = useState<any[]>([]);
  const [contractors, setContractors] = useState<any[]>([]);

  const [programForm] = Form.useForm();
  const [projectForm] = Form.useForm();
  const [hierarchyForm] = Form.useForm();
  const [userForm] = Form.useForm();
  const [assignForm] = Form.useForm();
  const [activityForm] = Form.useForm();
  const [contractorForm] = Form.useForm();

  useEffect(() => {
    loadPrograms();
    loadUsers();
    loadRoles();
  }, []);

  const loadPrograms = async () => {
    try {
      const res = await axios.get(`${API}/programs/`, { headers: getHeaders() });
      setPrograms(res.data);
    } catch {}
  };

  const loadUsers = async () => {
    try {
      const res = await axios.get(`${API}/users/`, { headers: getHeaders() });
      setUsers(res.data);
    } catch {}
  };

  const loadRoles = async () => {
    try {
      const res = await axios.get(`${API}/users/roles/`, { headers: getHeaders() });
      setRoles(res.data);
    } catch {}
  };

  const loadProjectsForProgram = async (programId: string) => {
    try {
      const res = await axios.get(`${API}/projects/`, { headers: getHeaders() });
      setExistingProjects(res.data.filter((p: any) => p.program === programId || p.program_id === programId));
    } catch {}
  };

  const selectProgram = (p: any) => {
    setCreatedProgram(p);
    loadProjectsForProgram(p.id);
    setCurrentStep(1);
    setSuccess(`Using program: ${p.name}`);
  };

  const handleCreateProgram = async (values: any) => {
    setLoading(true); setError('');
    try {
      const res = await axios.post(`${API}/programs/`, {
        ...values,
        start_date: values.start_date.format('YYYY-MM-DD'),
        expected_end_date: values.expected_end_date.format('YYYY-MM-DD'),
      }, { headers: getHeaders() });
      setCreatedProgram(res.data);
      setPrograms([...programs, res.data]);
      loadProjectsForProgram(res.data.id);
      setSuccess(`Program ${res.data.code} created!`);
      setCurrentStep(1);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create program.');
    } finally { setLoading(false); }
  };

  const selectProject = (p: any) => {
    setCreatedProject(p);
    setCurrentStep(2);
    setSuccess(`Using project: ${p.name}`);
  };

  const engagementMode = Form.useWatch('engagement_mode', projectForm);

  const handleCreateProject = async (values: any) => {
    setLoading(true); setError('');
    try {
      const res = await axios.post(`${API}/projects/`, {
        ...values,
        start_date: values.start_date.format('YYYY-MM-DD'),
        expected_end_date: values.expected_end_date.format('YYYY-MM-DD'),
        program_id: createdProgram?.id,
        hierarchy_template: { level1: 'Block', level2: 'Zone', level3: 'Row' },
      }, { headers: getHeaders() });
      setCreatedProject(res.data);
      setSuccess(`Project ${res.data.code} created!`);
      setCurrentStep(2);
    } catch (err: any) {
      setError(err.response?.data?.detail || JSON.stringify(err.response?.data) || 'Failed to create project.');
    } finally { setLoading(false); }
  };

  const handleAddHierarchyNode = async (values: any) => {
    setLoading(true); setError('');
    try {
      const res = await axios.post(
        `${API}/projects/${createdProject.id}/hierarchy/`,
        {
          code: values.code,
          name: values.name,
          level: parseInt(values.level),
          level_name: values.level === '1' ? 'Block' : values.level === '2' ? 'Zone' : 'Row',
          parent_id: values.parent_id || null,
        },
        { headers: getHeaders() }
      );
      setHierarchyNodes([...hierarchyNodes, res.data]);
      hierarchyForm.resetFields();
      setSuccess('Location node added!');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to add hierarchy node.');
    } finally { setLoading(false); }
  };

  const handleCreateUser = async (values: any) => {
    setLoading(true); setError('');
    try {
      const res = await axios.post(`${API}/users/`, values, { headers: getHeaders() });
      setUsers([...users, res.data]);
      userForm.resetFields();
      setSuccess(`User ${res.data.full_name} created!`);
    } catch (err: any) {
      setError(err.response?.data?.detail || JSON.stringify(err.response?.data) || 'Failed to create user.');
    } finally { setLoading(false); }
  };

  const handleAssignUser = async (values: any) => {
    setLoading(true); setError('');
    try {
      await axios.post(
        `${API}/projects/${createdProject.id}/members/`,
        { user_id: values.user_id, role_id: values.role_id, valid_from: new Date().toISOString().split('T')[0] },
        { headers: getHeaders() }
      );
      setAssignments([...assignments, {
        user: users.find(u => u.id === values.user_id)?.full_name,
        role: roles.find(r => r.id === values.role_id)?.name,
      }]);
      assignForm.resetFields();
      setSuccess('User assigned to project!');
    } catch (err: any) {
      setError('Failed to assign user.');
    } finally { setLoading(false); }
  };

  const handleAddActivity = async (values: any) => {
    setLoading(true); setError('');
    try {
      const res = await axios.post(
        `${API}/master/projects/${createdProject.id}/activities/`,
        values,
        { headers: getHeaders() }
      );
      setActivities([...activities, res.data]);
      activityForm.resetFields();
      setSuccess('Activity added!');
    } catch (err: any) {
      setError('Failed to add activity.');
    } finally { setLoading(false); }
  };

  const handleAddContractor = (values: any) => {
    setContractors([...contractors, values]);
    contractorForm.resetFields();
    setSuccess('Contractor added!');
  };

  const steps = [
    { title: 'Program', icon: <ApartmentOutlined /> },
    { title: 'Project', icon: <ProjectOutlined /> },
    { title: 'Hierarchy', icon: <ApartmentOutlined /> },
    { title: 'Team', icon: <TeamOutlined /> },
    { title: 'Activities', icon: <DatabaseOutlined /> },
    { title: 'Contractors', icon: <ToolOutlined /> },
    { title: 'Complete', icon: <CheckCircleOutlined /> },
  ];

  const cardStyle = { borderRadius: 12, marginBottom: 16 };
  const btnStyle = { background: '#1B2A4A', borderColor: '#1B2A4A' };

  return (
    <div style={{ padding: 24 }}>
      <Title level={4} style={{ color: '#1B2A4A', marginBottom: 24 }}>
        <SettingOutlined /> Admin Configuration — New Project Setup
      </Title>

      <Steps
        current={currentStep}
        style={{ marginBottom: 32 }}
        items={steps.map(s => ({ key: s.title, title: s.title, icon: s.icon }))}
      />

      {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} closable onClose={() => setError('')} />}
      {success && <Alert message={success} type="success" showIcon style={{ marginBottom: 16 }} closable onClose={() => setSuccess('')} />}

      {/* STEP 0: PROGRAM */}
      {currentStep === 0 && (
        <Card title="Step 1 — Select or Create Program" style={cardStyle}>
          <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            A Program groups related projects. Example: "Solar Projects Program".
          </Text>
          {programs.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <Text strong>Select existing program:</Text>
              <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {programs.map(p => (
                  <Button key={p.id} onClick={() => selectProgram(p)}>
                    {p.code} — {p.name}
                  </Button>
                ))}
              </div>
              <Divider>OR Create New Program</Divider>
            </div>
          )}
          <Form form={programForm} layout="vertical" onFinish={handleCreateProgram}>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="code" label="Program Code" rules={[{ required: true }]}>
                  <Input placeholder="e.g. BLD-PROG" />
                </Form.Item>
              </Col>
              <Col span={16}>
                <Form.Item name="name" label="Program Name" rules={[{ required: true }]}>
                  <Input placeholder="e.g. Building Projects Program" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="start_date" label="Start Date" rules={[{ required: true }]}>
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="expected_end_date" label="End Date" rules={[{ required: true }]}>
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={loading} style={btnStyle}>
                Create Program & Continue
              </Button>
            </Form.Item>
          </Form>
        </Card>
      )}

      {/* STEP 1: PROJECT */}
      {currentStep === 1 && (
        <Card title={`Step 2 — Select or Create Project under ${createdProgram?.name}`} style={cardStyle}>
          {existingProjects.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <Text strong>Select existing project:</Text>
              <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {existingProjects.map(p => (
                  <Button key={p.id} onClick={() => selectProject(p)}>
                    {p.code} — {p.name}
                  </Button>
                ))}
              </div>
              <Divider>OR Create New Project</Divider>
            </div>
          )}
          <Form form={projectForm} layout="vertical" onFinish={handleCreateProject}>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="code" label="Project Code" rules={[{ required: true }]}>
                  <Input placeholder="e.g. BLD001" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="project_type" label="Project Type" rules={[{ required: true }]}>
                  <Select options={[
                    { value: 'SOLAR', label: 'Solar' },
                    { value: 'BUILDING', label: 'Building' },
                    { value: 'BRIDGE', label: 'Bridge' },
                  ]} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="criticality" label="Criticality" rules={[{ required: true }]}>
                  <Select options={[
                    { value: 'CRITICAL', label: 'Critical' },
                    { value: 'HIGH', label: 'High' },
                    { value: 'MEDIUM', label: 'Medium' },
                  ]} />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="engagement_mode" label="How QACA is engaged" initialValue="PMC" rules={[{ required: true }]}>
                  <Select options={[
                    { value: 'PMC', label: 'Project Management Consultancy (PMC)' },
                    { value: 'AUDIT', label: 'Quality Inspection / Audit' },
                  ]} />
                </Form.Item>
              </Col>
              {engagementMode === 'AUDIT' && (
                <Col span={12}>
                  <Form.Item name="audit_pattern" label="Audit pattern" rules={[{ required: true, message: 'Choose the audit pattern' }]}>
                    <Select options={[
                      { value: 'VISIT', label: 'Visit-based (visiting auditors with reviewer)' },
                      { value: 'RESIDENT', label: 'Resident (stationed inspector)' },
                    ]} />
                  </Form.Item>
                </Col>
              )}
            </Row>
            <Form.Item name="name" label="Project Name" rules={[{ required: true }]}>
              <Input placeholder="Official project name" />
            </Form.Item>
            <Form.Item name="client_name" label="Client Name" rules={[{ required: true }]}>
              <Input placeholder="Client organisation name" />
            </Form.Item>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="client_abbr" label="Client abbreviation (for report numbers)">
                  <Input maxLength={20} placeholder="e.g. ABC" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="project_abbr" label="Project abbreviation (for report numbers)">
                  <Input maxLength={20} placeholder="e.g. TWR" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="start_date" label="Start Date" rules={[{ required: true }]}>
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="expected_end_date" label="End Date" rules={[{ required: true }]}>
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={loading} style={btnStyle}>
                Create Project & Continue
              </Button>
            </Form.Item>
          </Form>
        </Card>
      )}

      {/* STEP 2: HIERARCHY */}
      {currentStep === 2 && (
        <Card title={`Step 3 — Build Location Hierarchy for ${createdProject?.code}`} style={cardStyle}>
          <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            Add all location nodes: Level 1 = Block, Level 2 = Zone, Level 3 = Row/Cluster.
          </Text>
          <Form form={hierarchyForm} layout="vertical" onFinish={handleAddHierarchyNode}>
            <Row gutter={16}>
              <Col span={4}>
                <Form.Item name="level" label="Level" rules={[{ required: true }]}>
                  <Select options={[
                    { value: '1', label: 'Level 1 — Block' },
                    { value: '2', label: 'Level 2 — Zone' },
                    { value: '3', label: 'Level 3 — Row' },
                  ]} />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item name="code" label="Code" rules={[{ required: true }]}>
                  <Input placeholder="BLK-A" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="name" label="Name" rules={[{ required: true }]}>
                  <Input placeholder="Block A - 100MW North" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="parent_id" label="Parent Node (Level 2 & 3 only)">
                  <Select
                    allowClear
                    placeholder="Select parent"
                    options={hierarchyNodes.map(n => ({ value: n.id, label: `${n.code} — ${n.name}` }))}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={loading} icon={<PlusOutlined />} style={btnStyle}>
                Add Node
              </Button>
            </Form.Item>
          </Form>
          {hierarchyNodes.length > 0 && (
            <Table
              dataSource={hierarchyNodes}
              columns={[
                { title: 'Level', dataIndex: 'level', key: 'level', render: (l) => <Tag>Level {l}</Tag> },
                { title: 'Code', dataIndex: 'code', key: 'code' },
                { title: 'Name', dataIndex: 'name', key: 'name' },
              ]}
              pagination={false}
              rowKey="id"
              style={{ marginTop: 16 }}
            />
          )}
          <div style={{ marginTop: 24 }}>
            <Button onClick={() => setCurrentStep(3)} type="primary" style={btnStyle}>
              Next — Assign Team
            </Button>
          </div>
        </Card>
      )}

      {/* STEP 3: TEAM */}
      {currentStep === 3 && (
        <Card title={`Step 4 — Team Setup for ${createdProject?.code}`} style={cardStyle}>
          <Divider>Create New User</Divider>
          <Form form={userForm} layout="vertical" onFinish={handleCreateUser}>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="full_name" label="Full Name" rules={[{ required: true }]}>
                  <Input placeholder="John Smith" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="username" label="Username" rules={[{ required: true }]}>
                  <Input placeholder="john.smith" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email' }]}>
                  <Input placeholder="john@company.com" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="mobile" label="Mobile">
                  <Input placeholder="+91 98765 43210" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="password" label="Temporary Password" rules={[{ required: true }]}>
                  <Input.Password placeholder="Temp@1234" />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={loading} icon={<UserAddOutlined />} style={btnStyle}>
                Create User
              </Button>
            </Form.Item>
          </Form>

          <Divider>Assign User to Project</Divider>
          <Form form={assignForm} layout="vertical" onFinish={handleAssignUser}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="user_id" label="Select User" rules={[{ required: true }]}>
                  <Select
                    showSearch
                    placeholder="Select user"
                    options={users.map(u => ({ value: u.id, label: u.full_name }))}
                    filterOption={(input, option) =>
                      (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="role_id" label="Select Role" rules={[{ required: true }]}>
                  <Select
                    placeholder="Select role"
                    options={roles.map(r => ({ value: r.id, label: r.name }))}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={loading} icon={<PlusOutlined />} style={btnStyle}>
                Assign to Project
              </Button>
            </Form.Item>
          </Form>

          {assignments.length > 0 && (
            <Table
              dataSource={assignments}
              columns={[
                { title: 'User', dataIndex: 'user', key: 'user' },
                { title: 'Role', dataIndex: 'role', key: 'role', render: (r) => <Tag color="blue">{r}</Tag> },
              ]}
              pagination={false}
              rowKey="user"
              style={{ marginTop: 16 }}
            />
          )}
          <div style={{ marginTop: 24 }}>
            <Button onClick={() => setCurrentStep(4)} type="primary" style={btnStyle}>
              Next — Add Activities
            </Button>
          </div>
        </Card>
      )}

      {/* STEP 4: ACTIVITIES */}
      {currentStep === 4 && (
        <Card title={`Step 5 — Activities for ${createdProject?.code}`} style={cardStyle}>
          <Form form={activityForm} layout="vertical" onFinish={handleAddActivity}>
            <Row gutter={16}>
              <Col span={5}>
                <Form.Item name="code" label="Code" rules={[{ required: true }]}>
                  <Input placeholder="CIV-001" />
                </Form.Item>
              </Col>
              <Col span={10}>
                <Form.Item name="name" label="Activity Name" rules={[{ required: true }]}>
                  <Input placeholder="Site clearing and grubbing" />
                </Form.Item>
              </Col>
              <Col span={9}>
                <Form.Item name="discipline" label="Discipline" rules={[{ required: true }]}>
                  <Select options={[
                    { value: 'CIVIL', label: 'Civil' },
                    { value: 'ELECTRICAL', label: 'Electrical' },
                    { value: 'STRUCTURAL', label: 'Structural' },
                    { value: 'MECHANICAL', label: 'Mechanical' },
                    { value: 'COMMISSIONING', label: 'Commissioning' },
                    { value: 'GENERAL', label: 'General' },
                  ]} />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={loading} icon={<PlusOutlined />} style={btnStyle}>
                Add Activity
              </Button>
            </Form.Item>
          </Form>
          {activities.length > 0 && (
            <Table
              dataSource={activities}
              columns={[
                { title: 'Code', dataIndex: 'code', key: 'code' },
                { title: 'Name', dataIndex: 'name', key: 'name' },
                { title: 'Discipline', dataIndex: 'discipline', key: 'discipline', render: (d) => <Tag>{d}</Tag> },
              ]}
              pagination={false}
              rowKey="code"
              style={{ marginTop: 16 }}
            />
          )}
          <div style={{ marginTop: 24 }}>
            <Button onClick={() => setCurrentStep(5)} type="primary" style={btnStyle}>
              Next — Add Contractors
            </Button>
          </div>
        </Card>
      )}
      {/* STEP 5: CONTRACTORS */}
      {currentStep === 5 && (
        <Card title={`Step 6 — Contractors for ${createdProject?.code}`} style={cardStyle}>
          <Form form={contractorForm} layout="vertical" onFinish={handleAddContractor}>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="name" label="Contractor Name" rules={[{ required: true }]}>
                  <Input placeholder="ABC Contractors Pvt Ltd" />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item name="type" label="Type" rules={[{ required: true }]}>
                  <Select options={[
                    { value: 'GC', label: 'General Contractor' },
                    { value: 'SUB', label: 'Sub-Contractor' },
                    { value: 'VENDOR', label: 'Vendor' },
                  ]} />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="discipline" label="Scope">
                  <Input placeholder="Civil, Electrical, etc." />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="contact_name" label="Contact Person">
                  <Input placeholder="Site in-charge name" />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item>
              <Button type="primary" htmlType="submit" icon={<PlusOutlined />} style={btnStyle}>
                Add Contractor
              </Button>
            </Form.Item>
          </Form>
          {contractors.length > 0 && (
            <Table
              dataSource={contractors}
              columns={[
                { title: 'Name', dataIndex: 'name', key: 'name' },
                { title: 'Type', dataIndex: 'type', key: 'type', render: (t) => <Tag color="blue">{t}</Tag> },
                { title: 'Discipline', dataIndex: 'discipline', key: 'discipline' },
                { title: 'Contact', dataIndex: 'contact_name', key: 'contact_name' },
              ]}
              pagination={false}
              rowKey="name"
              style={{ marginTop: 16 }}
            />
          )}
          <div style={{ marginTop: 24 }}>
            <Button onClick={() => setCurrentStep(6)} type="primary" style={btnStyle}>
              Finish Setup
            </Button>
          </div>
        </Card>
      )}

      {/* STEP 6: COMPLETE */}
      {currentStep === 6 && (
        <Card style={{ ...cardStyle, textAlign: 'center', padding: 40 }}>
          <CheckCircleOutlined style={{ fontSize: 64, color: '#52c41a', marginBottom: 24 }} />
          <Title level={3} style={{ color: '#1B2A4A' }}>Project Setup Complete!</Title>
          <Text type="secondary">
            <strong>{createdProject?.code}</strong> — {createdProject?.name}
          </Text>
          <br /><br />
          <Row justify="center" gutter={32}>
            <Col><Text><strong>{hierarchyNodes.length}</strong> hierarchy nodes</Text></Col>
            <Col><Text><strong>{assignments.length}</strong> team members</Text></Col>
            <Col><Text><strong>{activities.length}</strong> activities</Text></Col>
            <Col><Text><strong>{contractors.length}</strong> contractors</Text></Col>
          </Row>
          <br /><br />
          <Button
            type="primary"
            style={btnStyle}
            onClick={() => {
              setCurrentStep(0);
              setCreatedProgram(null);
              setCreatedProject(null);
              setExistingProjects([]);
              setHierarchyNodes([]);
              setAssignments([]);
              setActivities([]);
              setContractors([]);
              programForm.resetFields();
              projectForm.resetFields();
            }}
          >
            Setup Another Project
          </Button>
        </Card>
      )}
    </div>
  );
};

export default AdminPage;